import { useProjectState } from '@common/git/Project/hooks';
import useGetProjectPipelines from '@common/project/hooks/useGetProjectPipelines';
import { fabricQueryKeys } from '@common/queries/fabricQueryKeys';
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { castArray, identity, isArray } from 'lodash-es';
import { useCallback, useMemo } from 'react';

import { SQL_PROVIDER } from '../../constants/sql';
import { useUser } from '../../context/user';
import { getOrchestrationRunsFabricToken } from '../../data/apis/api';
import { useRestMutation, useRestQuery } from '../../data/util';
import { Fabric } from '../../redux/types';
import { FROZEN_EMPTY_ARRAY } from '../constants';
import { MULTI_FABRIC_TOKEN_STALE_TIME } from './constants';
import { FabricProviderType } from './types';
import { ORCHESTRATION_PROVIDER, OrchestrationProviderInfo } from './types/orchestration';
import { SqlProvider } from './types/sql';
import { getFabricProviderInfo, isFabricExpired, isOrchestrationFabric } from './utils';

export function useUserFabrics(): Fabric[];
export function useUserFabrics<T>(fabricIds: T[], getter: (fabric: T) => string): Fabric[];
export function useUserFabrics<T>(fabricId: T, getter: (fabric: T) => string): Fabric;
export function useUserFabrics<T>(filterFabricIds?: T | T[], getter?: (fabric: T) => string) {
  const { user } = useUser();
  const fabrics = user?.fabrics || FROZEN_EMPTY_ARRAY;
  const persistantGetter = usePersistentCallback(getter || (() => undefined));
  const hasFilter = Boolean(filterFabricIds || getter);

  return useMemo(() => {
    if (!hasFilter) return fabrics;

    const fabricIdSet = new Set(castArray((filterFabricIds as T | T[]) || []).map(persistantGetter));
    const filteredFabrics = fabrics.filter((fabric) => fabricIdSet.has(fabric.id));
    return Array.isArray(filterFabricIds) ? filteredFabrics : filteredFabrics[0];
  }, [fabrics, filterFabricIds, persistantGetter, hasFilter]);
}

export type FabricWithProviderInfo = {
  fabric: Fabric;
  fabricProviderInfo: NonNullable<ReturnType<typeof getFabricProviderInfo>>;
  isExpired: boolean;
};

type FabricsWithProviderInfo<T extends Fabric | Fabric[] | undefined> = T extends Fabric
  ? FabricWithProviderInfo
  : T extends Fabric[]
    ? FabricWithProviderInfo[]
    : undefined;

export const useWithFabricProviderInfo = <T extends Fabric | Fabric[] | undefined>(fabrics: T) => {
  return useMemo(() => {
    const updatedFabrics = castArray(fabrics || []).map((fabric) => {
      const fabricProviderInfo = getFabricProviderInfo(fabric) as NonNullable<ReturnType<typeof getFabricProviderInfo>>;
      return {
        fabric,
        fabricProviderInfo,
        isExpired: isFabricExpired(fabric, fabricProviderInfo)
      };
    });

    return (isArray(fabrics) ? updatedFabrics : updatedFabrics[0]) as FabricsWithProviderInfo<T>;
  }, [fabrics]);
};

export function useSelectedFabric(fabricId?: string) {
  return useUserFabrics(fabricId, identity) as Fabric | undefined;
}

export function useOrchestrationFabricsInfoMap() {
  const fabrics = useUserFabrics();

  return useMemo(() => {
    const { orchestrationFabricIds, orchestrationFabricMap, orchestrationFabrics } = fabrics.reduce(
      (acc, fabric) => {
        if (isOrchestrationFabric(fabric)) {
          acc.orchestrationFabrics.push(fabric);
          acc.orchestrationFabricIds.push(Number(fabric.id));
          acc.orchestrationFabricMap.set(fabric.id, fabric);
        }
        return acc;
      },
      {
        orchestrationFabricIds: [] as number[],
        orchestrationFabricMap: new Map<string, Fabric>(),
        orchestrationFabrics: [] as Fabric[]
      }
    );

    return { orchestrationFabricIds, orchestrationFabricMap, orchestrationFabrics };
  }, [fabrics]);
}

export function useOrchestrationFabrics() {
  const { orchestrationFabricIds, orchestrationFabricMap, orchestrationFabrics } = useOrchestrationFabricsInfoMap();

  const {
    data: jwtData,
    isInitialLoading: isLoading,
    refetch
  } = useRestQuery(
    fabricQueryKeys.orchestrationFabricsToken(orchestrationFabricIds),
    () => getOrchestrationRunsFabricToken(orchestrationFabricIds),
    {
      enabled: !!orchestrationFabricIds.length
    }
  );

  const refetchJwt = useCallback(async () => {
    if (orchestrationFabricIds.length) {
      return refetch();
    }
    return false;
  }, [refetch, orchestrationFabricIds]);

  return {
    fabrics: orchestrationFabrics,
    fabricsMap: orchestrationFabricMap,
    jwt: jwtData?.data.jwt || '',
    isLoading,
    refetchJwt
  };
}

export function useGetMultiOrchFabricsToken() {
  const queryClient = useQueryClient();
  const { mutateAsync: getOrchestrationRunsFabricTokenAsync } = useRestMutation(
    (orchestrationFabricIds: number[]) => getOrchestrationRunsFabricToken(orchestrationFabricIds),
    {
      sentryTag: 'get-orchestration-runsfabric-token'
    }
  );

  const getJwt = async (orchestrationFabricIds: number[]) => {
    const queryKey = fabricQueryKeys.orchestrationFabricsToken(orchestrationFabricIds);

    const query = queryClient.getQueryCache().find(queryKey);

    if (!query?.state.data || query.state.dataUpdatedAt + MULTI_FABRIC_TOKEN_STALE_TIME < Date.now()) {
      const orchFabricTokenResponse = await getOrchestrationRunsFabricTokenAsync(orchestrationFabricIds);

      // even if key is not present, set the data in the query cache to avoid unnecessary re-fetching
      queryClient.setQueryData(queryKey, orchFabricTokenResponse);

      return orchFabricTokenResponse?.data.jwt;
    }

    return (query.state.data as Awaited<ReturnType<typeof getOrchestrationRunsFabricToken>>).data.jwt;
  };

  return getJwt;
}

type ComputedSQLFabricProviderInfo<T extends 'SQL' | 'ORCHESTRATOR'> = T extends 'SQL'
  ? {
      fabricProviderType: FabricProviderType.SQL;
      provider: SQL_PROVIDER;
      providerInfo: SqlProvider;
    }
  : {
      fabricProviderType: FabricProviderType.Orchestration;
      provider: ORCHESTRATION_PROVIDER;
      providerInfo: OrchestrationProviderInfo;
    };

/**
 * Returns a callback that computes the SQL or Orchestration provider info for a given fabric.
 *
 * For single-provider fabrics (old UX) or fabrics with only one provider type:
 * - Returns the provider info for whichever provider is present (SQL or Orchestration)
 *
 * For multi-provider fabrics (new UX) with both SQL and Orchestration providers:
 * - Returns SQL provider info if the project has no pipelines
 * - Returns Orchestration provider info if the project has pipelines
 *
 * @requires SqlState context (uses Redux selector for pipeline information)
 * @returns A callback that takes a Fabric and returns ComputedSQLFabricProviderInfo or undefined
 */
export function useGetSQLFabricProviderInfo() {
  const { isSimpleVersionControl } = useProjectState();
  const projectPipelines = useGetProjectPipelines();
  const hasPipelines = projectPipelines.length > 0;

  // returns a new callback reference when hasPipelines changes, allowing consumers to react to pipeline state changes
  return useCallback(
    (
      fabric: Fabric
    ): ComputedSQLFabricProviderInfo<'SQL'> | ComputedSQLFabricProviderInfo<'ORCHESTRATOR'> | undefined => {
      const fabricProviderInfo = getFabricProviderInfo(fabric);
      if (!fabricProviderInfo) {
        return undefined;
      }

      const { provider, sqlProviderInfo, orchestrationProviderInfo } = fabricProviderInfo;

      if (!sqlProviderInfo && !orchestrationProviderInfo) {
        return undefined;
      }

      const computedOrchestrationProviderInfo = {
        fabricProviderType: FabricProviderType.Orchestration,
        provider,
        providerInfo: orchestrationProviderInfo
      } as ComputedSQLFabricProviderInfo<'ORCHESTRATOR'>;

      const computedSqlProviderInfo = {
        fabricProviderType: FabricProviderType.SQL,
        provider,
        providerInfo: sqlProviderInfo
      } as ComputedSQLFabricProviderInfo<'SQL'>;

      const isSqlOnlyFabricProvider = sqlProviderInfo && !orchestrationProviderInfo;
      const isOrchestrationOnlyFabricProvider = orchestrationProviderInfo && !sqlProviderInfo;
      if (isSqlOnlyFabricProvider || isOrchestrationOnlyFabricProvider) {
        // if fabric only has one provider, return the provider info for the provider that is present
        return isOrchestrationOnlyFabricProvider ? computedOrchestrationProviderInfo : computedSqlProviderInfo;
      }

      // if project is simple version control, return orchestration provider info
      // if project has pipelines, return the orchestration provider info
      if (isSimpleVersionControl || hasPipelines) {
        return computedOrchestrationProviderInfo;
      }

      // return sqlprovider info if project is not simple version control and has no pipelines
      return computedSqlProviderInfo;
    },
    [hasPipelines, isSimpleVersionControl]
  );
}
